/**
 * Preview tints painted onto the real socket's vertex colours.
 *
 * Measure reduction tints each ring's band zone straight onto the socket's own
 * `color` attribute — that is deliberate, it is what lets the bands read *through*
 * the pen markings instead of hiding them. The tint is display-only: the handler
 * keeps the pristine colours in a ref and writes them back when the preview turns
 * off or the tool unmounts.
 *
 * That ref reaches the live attribute and nothing else, and every path that
 * *clones* the socket geometry runs while the preview is still up — the pre-apply
 * undo snapshot (taken by `execCommand` immediately before the tool executes), the
 * per-step mesh pool, and the before-mesh blob shipped from that snapshot. Each
 * one captured the band tint as though it were the socket's own colour. Restoring
 * any of them — a tool card's Undo, "discard and go back" — then brought the bands
 * back *permanently*, because the handler re-snapshots the restored colours as
 * pristine on its next paint and so has nothing left to revert to.
 *
 * So the pristine colours ride along on the geometry itself, and the capture sites
 * strip the tint from their copy. Marking is scoped to the real socket: the
 * shape-preview clone is tinted too, but it is a throwaway that never becomes a
 * snapshot, and its bake path has its own `__origColors` stash.
 */

/** userData key holding the pre-tint colour array. */
const PREVIEW_TINT_KEY = '__previewTintBase';

/**
 * Record the colours `geometry` had before a preview tint was painted over them.
 *
 * @param {import('three').BufferGeometry} geometry
 * @param {Float32Array|ArrayLike<number>} pristineColors — must not be mutated afterwards.
 */
export function markPreviewTint(geometry, pristineColors) {
  if (!geometry || !pristineColors) return;
  // Assign a fresh object rather than mutating in place: `BufferGeometry.copy`
  // shares `userData` by reference, so writing into the existing one would also
  // mark every clone already taken off this geometry — including the shape-preview
  // clone, whose vertices are deformed and whose colours these no longer describe.
  geometry.userData = { ...geometry.userData, [PREVIEW_TINT_KEY]: pristineColors };
}

/**
 * Forget the record, for when the tint has already been reverted on the live
 * attribute.
 *
 * @param {import('three').BufferGeometry} geometry
 */
export function clearPreviewTint(geometry) {
  if (!geometry?.userData?.[PREVIEW_TINT_KEY]) return;
  geometry.userData = { ...geometry.userData };
  delete geometry.userData[PREVIEW_TINT_KEY];
}

/** @param {import('three').BufferGeometry} geometry */
export function hasPreviewTint(geometry) {
  return !!geometry?.userData?.[PREVIEW_TINT_KEY];
}

/**
 * Put `geometry` back to its pre-tint colours, if a preview tint is on it.
 *
 * Safe to call on anything — a geometry that was never tinted is left untouched,
 * so capture sites can call it unconditionally rather than knowing which tool
 * happens to be open.
 *
 * @param {import('three').BufferGeometry} geometry
 * @returns {boolean} whether a tint was stripped
 */
export function stripPreviewTint(geometry) {
  const base = geometry?.userData?.[PREVIEW_TINT_KEY];
  if (!base) return false;

  const colAttr = geometry.attributes?.color;
  const usable = !!colAttr && colAttr.array.length === base.length;
  if (usable) {
    colAttr.array.set(base);
    colAttr.needsUpdate = true;
  }

  // `BufferGeometry.copy` assigns `userData` by *reference*, so a clone arrives
  // sharing the live socket's object. Give this geometry its own before dropping
  // the key — otherwise stripping a copy would also un-mark the socket still on
  // screen, stranding its tint with nothing to restore from.
  clearPreviewTint(geometry);
  return usable;
}

export default stripPreviewTint;
